<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import {
  fetchMarketplaceJson,
  postMarketplaceForm,
  postMarketplaceJsonBody,
} from 'dashboard/helpers/marketplaceApi';

const route = useRoute();
const accountId = route.params.accountId;

const COMPANIES = [
  { v: 'koza', l: 'Koza E-Ticaret' },
  { v: 'globaltech', l: 'Globaltech' },
];

const company = ref('koza');
const startDate = ref('');
const endDate = ref('');
const overview = ref(null);
const loading = ref(false);

const expanded = ref({}); // cari key -> bool
const cariInvoices = ref({}); // cari key -> invoice[]
const cariLoading = ref({});

// Yükleme
const fileInput = ref(null);
const uploading = ref(false);
const uploadMsg = ref('');
const companyLabel = computed(
  () => COMPANIES.find(c => c.v === company.value)?.l || ''
);

const base = () => `invoices/${company.value}`;
const dateParams = () => {
  const p = new URLSearchParams();
  if (startDate.value) p.set('start', startDate.value);
  if (endDate.value) p.set('end', endDate.value);
  return p;
};

const tl = v =>
  (v == null ? 0 : v).toLocaleString('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    maximumFractionDigits: 0,
  });
const usd = v =>
  (v == null ? 0 : v).toLocaleString('tr-TR', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

const groups = computed(() => overview.value?.groups || []);

// Kâr-Zarara Esas Gider (cari seviyesi işaret)
const draggingCari = ref(null);
const pnlZoneActive = ref(false);
const pnlArmed = computed(() => draggingCari.value !== null);
const pnlGroups = computed(() => groups.value.filter(g => g.pnl));
const pnlTryAbs = computed(() => Math.abs(overview.value?.pnl_try || 0));
const pnlUsdVal = computed(() => overview.value?.pnl_usd || 0);

const loadOverview = async () => {
  overview.value = await fetchMarketplaceJson(
    accountId,
    `${base()}/overview?${dateParams().toString()}`
  );
};

const loadCari = async key => {
  cariLoading.value = { ...cariLoading.value, [key]: true };
  try {
    const p = dateParams();
    p.set('cari', key);
    p.set('per_page', '5000');
    const res = await fetchMarketplaceJson(
      accountId,
      `${base()}/list?${p.toString()}`
    );
    cariInvoices.value = { ...cariInvoices.value, [key]: res.invoices };
  } finally {
    cariLoading.value = { ...cariLoading.value, [key]: false };
  }
};

const toggle = async key => {
  const next = !expanded.value[key];
  expanded.value = { ...expanded.value, [key]: next };
  if (next && !cariInvoices.value[key]) await loadCari(key);
};

const reload = async () => {
  loading.value = true;
  expanded.value = {};
  cariInvoices.value = {};
  try {
    await loadOverview();
  } finally {
    loading.value = false;
  }
};

const switchCompany = async v => {
  company.value = v;
  uploadMsg.value = '';
  await reload();
};

// Bir cari'yi Kâr-Zarara dahil et/çıkar (🎯 buton, havuza sürükle, çip ✕).
const setCariPnl = async (key, pnl) => {
  await postMarketplaceJsonBody(accountId, `${base()}/cari/pnl`, {
    cari: key,
    pnl,
  });
  await reload();
};
const togglePnl = g => setCariPnl(g.key, !g.pnl);
const removePnlCari = key => setCariPnl(key, false);

const onCariDragStart = g => {
  draggingCari.value = g;
};
const onPnlOver = ev => {
  if (!draggingCari.value) return;
  ev.preventDefault();
  pnlZoneActive.value = true;
};
const onPnlDrop = async () => {
  const g = draggingCari.value;
  draggingCari.value = null;
  pnlZoneActive.value = false;
  if (g) await setCariPnl(g.key, true);
};

const onUpload = async e => {
  const file = e.target.files?.[0];
  if (!file) return;
  uploading.value = true;
  uploadMsg.value = '';
  try {
    const fd = new FormData();
    fd.append('file', file);
    const res = await postMarketplaceForm(accountId, `${base()}/upload`, fd);
    uploadMsg.value = `✓ ${res.created} yeni fatura eklendi, ${res.updated} mevcut fatura güncellendi.`;
    await reload();
  } catch (err) {
    uploadMsg.value = `Hata: ${err.message}`;
  } finally {
    uploading.value = false;
    if (fileInput.value) fileInput.value.value = '';
  }
};

onMounted(reload);
</script>

<template>
  <div class="flex flex-col h-full overflow-auto bg-slate-25 dark:bg-slate-900">
    <div class="p-4 mx-auto w-full max-w-screen-xl">
      <!-- Başlık + şirket sekmeleri -->
      <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 class="text-2xl font-semibold text-slate-900 dark:text-slate-50">
          Fatura
        </h1>
        <div class="flex p-1 bg-slate-100 rounded-lg dark:bg-slate-800">
          <button
            v-for="c in COMPANIES"
            :key="c.v"
            class="px-4 py-1.5 text-sm font-medium rounded-md transition-colors"
            :class="
              company === c.v
                ? 'bg-white shadow text-slate-900 dark:bg-slate-700 dark:text-white'
                : 'text-slate-500 dark:text-slate-400'
            "
            @click="switchCompany(c.v)"
          >
            {{ c.l }}
          </button>
        </div>
      </div>

      <!-- Tarih filtresi -->
      <div class="flex flex-wrap items-end gap-3 mb-4">
        <label class="flex flex-col text-xs text-slate-500">
          Başlangıç
          <input
            v-model="startDate"
            type="date"
            class="mt-1 px-2 py-1 text-sm border rounded border-slate-200 dark:bg-slate-800 dark:border-slate-700"
          />
        </label>
        <label class="flex flex-col text-xs text-slate-500">
          Bitiş
          <input
            v-model="endDate"
            type="date"
            class="mt-1 px-2 py-1 text-sm border rounded border-slate-200 dark:bg-slate-800 dark:border-slate-700"
          />
        </label>
        <button
          class="px-3 py-1.5 text-sm text-white rounded bg-woot-500 hover:bg-woot-600"
          @click="reload"
        >
          Uygula
        </button>
        <span v-if="loading" class="text-sm text-slate-400">Yükleniyor…</span>
      </div>

      <!-- Fatura listesi yükle -->
      <div
        class="flex flex-wrap items-center gap-3 p-3 mb-4 border rounded-lg bg-woot-25 border-woot-100 dark:bg-woot-900/20 dark:border-woot-800"
      >
        <span class="text-sm text-slate-700 dark:text-slate-200">
          📥 <b>{{ companyLabel }}</b> fatura listesini yükle (Doğan Dönüşüm
          .xls)
        </span>
        <input
          ref="fileInput"
          type="file"
          accept=".xls"
          class="hidden"
          @change="onUpload"
        />
        <button
          class="px-3 py-1.5 text-sm text-white rounded bg-woot-500 hover:bg-woot-600 disabled:opacity-50"
          :disabled="uploading"
          @click="fileInput?.click()"
        >
          {{ uploading ? 'İşleniyor…' : 'Dosya seç ve güncelle' }}
        </button>
        <span
          v-if="uploadMsg"
          class="text-sm font-medium text-slate-700 dark:text-slate-200"
        >
          {{ uploadMsg }}
        </span>
        <span class="text-xs text-slate-400">
          Eski faturalar korunur, sadece yenisi eklenir; her güncel listeyi
          yükleyince kendini günceller.
        </span>
      </div>

      <!-- Özet kartları -->
      <div v-if="overview" class="grid grid-cols-2 gap-3 mb-4 sm:grid-cols-4">
        <div class="p-4 bg-white rounded-lg shadow-sm dark:bg-slate-800">
          <div class="text-xs text-slate-500">
            Toplam Gider (₺, İade düşülmüş)
          </div>
          <div class="text-xl font-semibold text-ruby-700 dark:text-ruby-400">
            {{ tl(overview.try_total) }}
          </div>
        </div>
        <div class="p-4 bg-white rounded-lg shadow-sm dark:bg-slate-800">
          <div class="text-xs text-slate-500">Döviz Faturalar (USD)</div>
          <div class="text-xl font-semibold text-slate-700 dark:text-slate-200">
            {{ usd(overview.usd_total) }}
          </div>
        </div>
        <div class="p-4 bg-white rounded-lg shadow-sm dark:bg-slate-800">
          <div class="text-xs text-slate-500">Fatura / Cari</div>
          <div class="text-xl font-semibold text-slate-700 dark:text-slate-200">
            {{ overview.count }} / {{ overview.cari_count }}
          </div>
        </div>
        <div class="p-4 bg-white rounded-lg shadow-sm dark:bg-slate-800">
          <div class="text-xs text-slate-500">Dönem / İade</div>
          <div class="text-sm font-medium text-slate-600 dark:text-slate-300">
            {{ overview.date_min }} → {{ overview.date_max }}
          </div>
          <div class="text-xs text-slate-400">
            {{ overview.refund_count }} iade faturası
          </div>
        </div>
      </div>

      <!-- Cari (firma) bazında gruplar -->
      <div class="flex flex-col gap-2">
        <div
          v-for="g in groups"
          :key="g.key"
          class="bg-white rounded-lg shadow-sm dark:bg-slate-800"
        >
          <div class="flex items-center">
            <span
              class="px-2 select-none cursor-grab text-slate-300 hover:text-slate-500 shrink-0"
              draggable="true"
              title="Kâr-Zarara Esas Gider'e sürükle"
              @dragstart="onCariDragStart(g)"
              @dragend="draggingCari = null"
              >⠿</span
            >
            <button
              class="flex items-center justify-between flex-1 gap-3 px-2 py-3 text-left min-w-0"
              @click="toggle(g.key)"
            >
              <div class="flex items-center gap-2 min-w-0">
                <span class="text-slate-400">{{
                  expanded[g.key] ? '▾' : '▸'
                }}</span>
                <span
                  class="font-medium truncate text-slate-800 dark:text-slate-100"
                >
                  {{ g.label }}
                </span>
                <span
                  class="px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300"
                >
                  {{ g.count }} fatura
                </span>
                <span
                  v-if="g.refunds > 0"
                  class="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                >
                  {{ g.refunds }} iade
                </span>
                <span
                  v-if="g.pnl"
                  class="px-1.5 py-0.5 text-[10px] font-bold rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300"
                  title="Kâr-Zarara Esas Gider'e dahil"
                  >K/Z</span
                >
              </div>
              <div class="text-right shrink-0">
                <div
                  class="font-semibold tabular-nums text-ruby-700 dark:text-ruby-400"
                >
                  {{ tl(g.try) }}
                </div>
                <div
                  v-if="g.usd !== 0"
                  class="text-xs tabular-nums text-slate-500"
                >
                  {{ usd(g.usd) }}
                </div>
              </div>
            </button>
            <button
              class="px-2 mr-2 text-lg rounded shrink-0 hover:bg-indigo-100 dark:hover:bg-indigo-900/40"
              :class="
                g.pnl ? 'opacity-100' : 'opacity-40 hover:opacity-100 grayscale'
              "
              :title="
                g.pnl
                  ? 'Kâr-Zarara Esas Gider\'den çıkar'
                  : 'Kâr-Zarara Esas Gider\'e ekle'
              "
              @click="togglePnl(g)"
            >
              🎯
            </button>
          </div>

          <!-- Açılınca: faturalar -->
          <div
            v-if="expanded[g.key]"
            class="border-t border-slate-100 dark:border-slate-700"
          >
            <div
              v-if="cariLoading[g.key]"
              class="px-4 py-3 text-sm text-slate-400"
            >
              Yükleniyor…
            </div>
            <table v-else class="w-full text-sm">
              <thead>
                <tr class="text-xs text-left text-slate-400">
                  <th class="px-4 py-2 font-normal">Tarih</th>
                  <th class="px-4 py-2 font-normal">Belge No</th>
                  <th class="px-4 py-2 font-normal">Tip</th>
                  <th class="px-4 py-2 font-normal text-right">Matrah</th>
                  <th class="px-4 py-2 font-normal text-right">KDV</th>
                  <th class="px-4 py-2 font-normal text-right">Ödenecek</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="inv in cariInvoices[g.key] || []"
                  :key="inv.id"
                  class="border-t border-slate-50 dark:border-slate-700/50"
                  :class="inv.refund ? 'bg-amber-50 dark:bg-amber-900/10' : ''"
                >
                  <td
                    class="px-4 py-2 whitespace-nowrap text-slate-600 dark:text-slate-300"
                  >
                    <span
                      v-if="inv.is_recent"
                      class="px-1.5 py-0.5 mr-1.5 text-xs font-bold rounded bg-woot-500 text-white"
                      >Yeni</span
                    >
                    {{ inv.invoice_on }}
                  </td>
                  <td class="px-4 py-2 text-slate-500 dark:text-slate-400">
                    {{ inv.belge_no }}
                  </td>
                  <td class="px-4 py-2 text-slate-500 dark:text-slate-400">
                    {{ inv.tip }}
                  </td>
                  <td class="px-4 py-2 text-right tabular-nums text-slate-500">
                    {{ inv.matrah.toLocaleString('tr-TR') }}
                  </td>
                  <td class="px-4 py-2 text-right tabular-nums text-slate-500">
                    {{ inv.total_kdv.toLocaleString('tr-TR') }}
                  </td>
                  <td
                    class="px-4 py-2 text-right font-medium tabular-nums"
                    :class="
                      inv.refund
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-slate-700 dark:text-slate-200'
                    "
                  >
                    {{ inv.refund ? '−' : ''
                    }}{{ inv.amount.toLocaleString('tr-TR') }}
                    {{ inv.currency === 'USD' ? '$' : '₺' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div
          v-if="overview && groups.length === 0"
          class="p-6 text-sm text-center text-slate-400"
        >
          Bu şirket için henüz fatura yok. Yukarıdan Doğan Dönüşüm .xls
          listesini yükle.
        </div>
      </div>

      <!-- KÂR-ZARARA ESAS GİDER (cari sürükle-bırak havuzu) -->
      <div
        class="mt-6 border-2 rounded-xl transition-colors"
        :class="
          pnlZoneActive
            ? 'border-indigo-500 border-dashed bg-indigo-100 dark:bg-indigo-900/40'
            : pnlArmed
              ? 'border-indigo-400 border-dashed bg-indigo-50 dark:bg-indigo-900/25'
              : 'border-indigo-200 bg-indigo-50/40 dark:border-indigo-800 dark:bg-indigo-900/10'
        "
        @dragenter.prevent
        @dragover="onPnlOver"
        @dragleave="pnlZoneActive = false"
        @drop.prevent="onPnlDrop"
      >
        <div
          v-if="pnlArmed"
          class="px-4 py-2 text-sm font-semibold text-center text-indigo-600 dark:text-indigo-300"
        >
          ↓ Buraya bırak — bu firmayı Kâr-Zarara dahil et
        </div>
        <div class="flex flex-wrap items-center justify-between gap-3 p-4">
          <div class="min-w-0">
            <div
              class="text-sm font-bold tracking-wide uppercase text-indigo-700 dark:text-indigo-300"
            >
              🎯 Kâr-Zarara Esas Gider
            </div>
            <div class="max-w-xl text-xs text-slate-500 dark:text-slate-400">
              Banka defterinde görünmeyen firmaların gider faturalarını buraya
              al. Bir firmayı eklemek için kartındaki 🎯 butonuna bas ya da ⠿
              tutamağından buraya sürükle. (Kargo/komisyon gibi satışta zaten
              sayılanları EKLEME.)
            </div>
          </div>
          <div class="text-right shrink-0">
            <div class="text-xs text-slate-500">İşaretli toplam gider</div>
            <div
              class="text-2xl font-bold tabular-nums text-indigo-700 dark:text-indigo-300"
            >
              {{ tl(pnlTryAbs) }}
            </div>
            <div
              v-if="pnlUsdVal !== 0"
              class="text-xs tabular-nums text-slate-500"
            >
              {{ usd(pnlUsdVal) }}
            </div>
          </div>
        </div>

        <div class="px-4 pb-4">
          <div
            v-if="pnlGroups.length === 0"
            class="py-3 text-sm text-center border border-dashed rounded-lg text-indigo-400 border-indigo-200 dark:border-indigo-800"
          >
            Henüz boş — bir firmanın kartındaki 🎯 butonuna bas ya da ⠿
            tutamağından buraya sürükle.
          </div>
          <div v-else class="flex flex-wrap gap-2">
            <span
              v-for="g in pnlGroups"
              :key="g.key"
              class="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-white border rounded-full shadow-sm border-indigo-200 dark:bg-slate-800 dark:border-indigo-800"
            >
              <span class="text-slate-700 dark:text-slate-200">{{
                g.label
              }}</span>
              <span class="tabular-nums text-ruby-600 dark:text-ruby-400">{{
                tl(g.try)
              }}</span>
              <button
                class="text-slate-400 hover:text-ruby-500"
                title="Çıkar"
                @click="removePnlCari(g.key)"
              >
                ✕
              </button>
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
